import { errorMessage } from "@atlas/shared";
import React from "react";
import { useMutation } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { Bug, ImagePlus, Lightbulb, MapPinPlus, MessageSquarePlus, X } from "lucide-react";
import { Button } from "./Button";
import { useAuth } from "../hooks/useAuth";
import { getFeedbackPhotoUploadUrl, submitFeedback, type FeedbackType } from "../api/feedback";

// Global "Send feedback" modal. Reachable from the sidebar on any protected
// page so it can auto-capture where the user was (current URL). Feedback lands
// in the DB and is mirrored to a Linear card by the sync worker.

const TYPE_OPTIONS: { value: FeedbackType; label: string; icon: typeof Bug; hint: string }[] = [
  { value: "bug", label: "Bug", icon: Bug, hint: "Something is broken or wrong" },
  { value: "feature", label: "Feature", icon: Lightbulb, hint: "An idea or improvement" },
  { value: "data", label: "Missing data", icon: MapPinPlus, hint: "A missing peak, landmark, trail or region" },
  { value: "other", label: "Other", icon: MessageSquarePlus, hint: "Anything else" },
];

const DATA_ITEM_KINDS = ["peak", "landmark", "trail", "region", "other"] as const;

const MAX_PHOTOS = 3;
const MAX_PHOTO_BYTES = 5 * 1024 * 1024;
const ALLOWED_PHOTO_TYPES = ["image/jpeg", "image/png", "image/webp"];

interface PendingPhoto {
  localId: string;
  previewUrl: string;
  key: string | null;
  uploading: boolean;
  error: string | null;
}

const inputStyle: React.CSSProperties = {
  width: "100%",
  height: "var(--control-md)",
  padding: "0 12px",
  background: "var(--surface-sunken)",
  border: "1px solid var(--border-default)",
  borderRadius: "var(--radius-md)",
  color: "var(--text-primary)",
  fontSize: "var(--text-base)",
  fontFamily: "var(--font-sans)",
  outline: "none",
  boxSizing: "border-box",
};

const textareaStyle: React.CSSProperties = {
  ...inputStyle,
  height: "auto",
  minHeight: 76,
  padding: "10px 12px",
  resize: "vertical",
  lineHeight: 1.5,
};

const labelStyle: React.CSSProperties = {
  display: "block",
  fontSize: 13,
  fontWeight: 600,
  color: "var(--text-secondary)",
  marginBottom: 6,
};

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <label style={labelStyle}>{label}</label>
      {children}
    </div>
  );
}

export function FeedbackDialog({ onClose }: { onClose: () => void }) {
  const { user } = useAuth();
  const [type, setType] = React.useState<FeedbackType>("bug");
  const [title, setTitle] = React.useState("");
  const [body, setBody] = React.useState("");
  const [extra, setExtra] = React.useState<Record<string, string>>({});
  const [photos, setPhotos] = React.useState<PendingPhoto[]>([]);
  const [done, setDone] = React.useState(false);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    // Revoke preview object URLs when the dialog unmounts.
    return () => {
      setPhotos((prev) => {
        prev.forEach((p) => URL.revokeObjectURL(p.previewUrl));
        return prev;
      });
    };
  }, []);

  async function handleFiles(fileList: FileList) {
    const remaining = MAX_PHOTOS - photos.length;
    const files = Array.from(fileList).slice(0, Math.max(0, remaining));
    for (const file of files) {
      if (!ALLOWED_PHOTO_TYPES.includes(file.type)) {
        window.alert("Screenshots must be JPEG, PNG or WebP");
        continue;
      }
      if (file.size > MAX_PHOTO_BYTES) {
        window.alert(`${file.name} is over 5 MB`);
        continue;
      }

      const localId = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      const previewUrl = URL.createObjectURL(file);
      setPhotos((prev) => [...prev, { localId, previewUrl, key: null, uploading: true, error: null }]);

      try {
        const { uploadUrl, key } = await getFeedbackPhotoUploadUrl(file.type);
        const uploadRes = await fetch(uploadUrl, {
          method: "PUT",
          body: file,
          headers: { "Content-Type": file.type },
        });
        if (!uploadRes.ok) throw new Error("Upload failed");
        setPhotos((prev) => prev.map((p) => (p.localId === localId ? { ...p, key, uploading: false } : p)));
      } catch (err) {
        setPhotos((prev) =>
          prev.map((p) =>
            p.localId === localId
              ? { ...p, uploading: false, error: errorMessage(err, "Upload failed") }
              : p,
          ),
        );
      }
    }
  }

  function removePhoto(localId: string) {
    setPhotos((prev) => {
      const target = prev.find((p) => p.localId === localId);
      if (target) URL.revokeObjectURL(target.previewUrl);
      return prev.filter((p) => p.localId !== localId);
    });
  }

  // Reset per-type fields when the type changes so stale values don't leak into
  // the wrong `details` shape.
  function selectType(next: FeedbackType) {
    setType(next);
    setExtra({});
  }

  const set = (key: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) =>
    setExtra((prev) => ({ ...prev, [key]: e.target.value }));

  function buildDetails(): Record<string, unknown> {
    switch (type) {
      case "bug":
        return {
          stepsToReproduce: extra["stepsToReproduce"] ?? "",
          expected: extra["expected"] ?? "",
          actual: extra["actual"] ?? "",
        };
      case "feature":
        return {
          problem: extra["problem"] ?? "",
          proposedSolution: extra["proposedSolution"] ?? "",
        };
      case "data":
        return {
          itemKind: extra["itemKind"] ?? "peak",
          name: extra["name"] ?? "",
          region: extra["region"] ?? "",
          coords: extra["coords"] ?? "",
          sourceUrl: extra["sourceUrl"] ?? "",
        };
      default:
        return {};
    }
  }

  const readyPhotos = photos.filter((p) => p.key && !p.uploading && !p.error);
  const anyUploading = photos.some((p) => p.uploading);

  const mutation = useMutation({
    mutationFn: () =>
      submitFeedback({
        type,
        title: title.trim(),
        body: body.trim(),
        details: buildDetails(),
        context: {
          url: window.location.href,
          userAgent: navigator.userAgent,
          plan: user?.plan ?? null,
        },
        photos: readyPhotos.length ? readyPhotos.map((p) => p.key!) : undefined,
      }),
    onSuccess: () => setDone(true),
  });

  const canSubmit = title.trim().length > 0 && body.trim().length > 0 && !anyUploading && !mutation.isPending;

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,.6)",
        backdropFilter: "blur(4px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
        padding: 16,
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        style={{
          background: "var(--surface-card)",
          border: "1px solid var(--border-strong)",
          borderRadius: "var(--radius-xl)",
          padding: 28,
          maxWidth: 480,
          width: "100%",
          maxHeight: "calc(100vh - 32px)",
          overflowY: "auto",
          boxShadow: "var(--shadow-xl)",
        }}
      >
        {done ? (
          <>
            <h2 style={{ font: "var(--type-h3)", color: "var(--text-primary)", marginBottom: 8 }}>
              Thanks — we've got it
            </h2>
            <p style={{ fontSize: 14, color: "var(--text-secondary)", marginBottom: 20, lineHeight: 1.5 }}>
              Your {TYPE_OPTIONS.find((o) => o.value === type)?.label.toLowerCase()} feedback has
              been logged. We review everything, and if we reply you'll get an email and a
              notification here. You can follow the thread under{" "}
              <Link to="/feedback" onClick={onClose} style={{ color: "var(--text-accent)" }}>
                Your feedback
              </Link>
              .
            </p>
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <Button variant="primary" onClick={onClose}>Done</Button>
            </div>
          </>
        ) : (
          <>
            <h2 style={{ font: "var(--type-h3)", color: "var(--text-primary)", marginBottom: 4 }}>
              Send feedback
            </h2>
            <p style={{ fontSize: 14, color: "var(--text-secondary)", marginBottom: 18, lineHeight: 1.5 }}>
              Report a bug, request a feature, or flag missing map data.
            </p>

            {/* Type selector */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 18 }}>
              {TYPE_OPTIONS.map((opt) => {
                const active = type === opt.value;
                const Icon = opt.icon;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => selectType(opt.value)}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      padding: "10px 12px",
                      borderRadius: "var(--radius-md)",
                      background: active ? "var(--accent-soft)" : "var(--surface-sunken)",
                      border: `1px solid ${active ? "var(--border-gold)" : "var(--border-subtle)"}`,
                      color: active ? "var(--text-primary)" : "var(--text-secondary)",
                      cursor: "pointer",
                      textAlign: "left",
                      fontSize: 13,
                      fontWeight: 600,
                      fontFamily: "var(--font-sans)",
                    }}
                  >
                    <Icon size={16} style={{ flexShrink: 0 }} />
                    {opt.label}
                  </button>
                );
              })}
            </div>

            <Field label="Title">
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                autoFocus
                placeholder="A short summary"
                style={inputStyle}
              />
            </Field>

            <Field label="Description">
              <textarea
                value={body}
                onChange={(e) => setBody(e.target.value)}
                placeholder="Tell us more"
                style={textareaStyle}
              />
            </Field>

            {/* Per-type fields */}
            {type === "bug" && (
              <>
                <Field label="Steps to reproduce">
                  <textarea value={extra["stepsToReproduce"] ?? ""} onChange={set("stepsToReproduce")} style={textareaStyle} />
                </Field>
                <Field label="What you expected">
                  <input type="text" value={extra["expected"] ?? ""} onChange={set("expected")} style={inputStyle} />
                </Field>
                <Field label="What happened instead">
                  <input type="text" value={extra["actual"] ?? ""} onChange={set("actual")} style={inputStyle} />
                </Field>
              </>
            )}

            {type === "feature" && (
              <>
                <Field label="What problem would this solve?">
                  <textarea value={extra["problem"] ?? ""} onChange={set("problem")} style={textareaStyle} />
                </Field>
                <Field label="Proposed solution (optional)">
                  <textarea value={extra["proposedSolution"] ?? ""} onChange={set("proposedSolution")} style={textareaStyle} />
                </Field>
              </>
            )}

            {type === "data" && (
              <>
                <Field label="What kind of thing is missing?">
                  <select value={extra["itemKind"] ?? "peak"} onChange={set("itemKind")} style={inputStyle}>
                    {DATA_ITEM_KINDS.map((k) => (
                      <option key={k} value={k}>{k[0]!.toUpperCase() + k.slice(1)}</option>
                    ))}
                  </select>
                </Field>
                <Field label="Name">
                  <input type="text" value={extra["name"] ?? ""} onChange={set("name")} placeholder="e.g. Foel Fenlli" style={inputStyle} />
                </Field>
                <Field label="Region / area (optional)">
                  <input type="text" value={extra["region"] ?? ""} onChange={set("region")} placeholder="e.g. Clwydian Range" style={inputStyle} />
                </Field>
                <Field label="Coordinates (optional)">
                  <input type="text" value={extra["coords"] ?? ""} onChange={set("coords")} placeholder="lat, lng" style={inputStyle} />
                </Field>
                <Field label="Source link (optional)">
                  <input type="text" value={extra["sourceUrl"] ?? ""} onChange={set("sourceUrl")} placeholder="A reference URL" style={inputStyle} />
                </Field>
              </>
            )}

            <Field label="Screenshots (optional)">
              <input
                ref={fileInputRef}
                type="file"
                accept={ALLOWED_PHOTO_TYPES.join(",")}
                multiple
                style={{ display: "none" }}
                onChange={(e) => {
                  if (e.target.files?.length) void handleFiles(e.target.files);
                  e.target.value = "";
                }}
              />
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                {photos.map((p) => (
                  <div
                    key={p.localId}
                    style={{
                      position: "relative",
                      width: 64,
                      height: 64,
                      borderRadius: "var(--radius-md)",
                      overflow: "hidden",
                      border: `1px solid ${p.error ? "var(--status-danger)" : "var(--border-default)"}`,
                      opacity: p.uploading ? 0.5 : 1,
                    }}
                  >
                    <img
                      src={p.previewUrl}
                      alt=""
                      style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                    />
                    <button
                      type="button"
                      onClick={() => removePhoto(p.localId)}
                      aria-label="Remove screenshot"
                      style={{
                        position: "absolute",
                        top: 2,
                        right: 2,
                        width: 20,
                        height: 20,
                        borderRadius: "50%",
                        background: "rgba(0,0,0,.65)",
                        border: "none",
                        color: "#fff",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        cursor: "pointer",
                        padding: 0,
                      }}
                    >
                      <X size={12} />
                    </button>
                  </div>
                ))}
                {photos.length < MAX_PHOTOS && (
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    style={{
                      width: 64,
                      height: 64,
                      borderRadius: "var(--radius-md)",
                      background: "var(--surface-sunken)",
                      border: "1px dashed var(--border-default)",
                      color: "var(--text-secondary)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: "pointer",
                    }}
                    aria-label="Add screenshot"
                  >
                    <ImagePlus size={18} />
                  </button>
                )}
              </div>
              {photos.some((p) => p.error) && (
                <p style={{ fontSize: 12, color: "var(--status-danger)", marginTop: 6 }}>
                  Some screenshots failed to upload — remove them and try again.
                </p>
              )}
            </Field>

            {mutation.isError && (
              <p style={{ fontSize: 13, color: "var(--status-danger)", marginBottom: 12 }}>
                {errorMessage(mutation.error, "Failed to send feedback")}
              </p>
            )}

            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 6 }}>
              <Button variant="ghost" onClick={onClose}>Cancel</Button>
              <Button variant="primary" disabled={!canSubmit} onClick={() => mutation.mutate()}>
                {mutation.isPending ? "Sending…" : "Send feedback"}
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
